/**
 * Resolve the segment (from → to) at which a breakdown occurred.
 *
 * Backend does not carry the segment on `Breakdown` directly — the breakdown
 * location is a single `station` reference, and it can be a synthetic
 * X-station that never appears in the ride's real station list. Callers do
 * have access to `workAssignmentSequences`, so we mirror the same detection
 * logic that RideDetails and WorkAssignmentCard use to place the virtual
 * breakdown station:
 *
 * - Find the sequence whose `fromStation.id` or `toStation.id` matches
 *   `breakdown.station.id`.
 * - Inside that sequence's `workAssignments[]`, find the sub-segment whose
 *   endpoint matches the same id. That sub-segment IS the answer.
 * - If the sequence has no `workAssignments`, fall back to
 *   `{ fromStation, toStation }` of the sequence itself.
 *
 * Returns `null` when no matching sequence is found — the caller then omits
 * the Segment row entirely.
 *
 * @param {object} breakdown  Non-null `Breakdown` payload.
 * @param {Array<{ fromStation?: object, toStation?: object, workAssignments?: Array<{ fromStation?: object, toStation?: object }> }>} sequences
 * @returns {{ fromStation: object, toStation: object } | null}
 */
export const deriveBreakdownSegment = (breakdown, sequences) => {
	const stationId = breakdown?.station?.id;
	if (!stationId || !Array.isArray(sequences)) return null;

	const matchingSequence = sequences.find(
		(seq) => seq?.fromStation?.id === stationId || seq?.toStation?.id === stationId
	);
	if (!matchingSequence) return null;

	const subSegments = Array.isArray(matchingSequence.workAssignments)
		? matchingSequence.workAssignments
		: [];
	const subSegment = subSegments.find(
		(wa) => wa?.fromStation?.id === stationId || wa?.toStation?.id === stationId
	);

	const source = subSegment ?? matchingSequence;
	if (!source?.fromStation || !source?.toStation) return null;

	return { fromStation: source.fromStation, toStation: source.toStation };
};
